import SettingsIcon from '@mui/icons-material/Settings'
import Business from '@mui/icons-material/Business'
import ArrowForward from '@mui/icons-material/ArrowForward'
import { useContext, useEffect, useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ROLE_TYPES } from 'src/consumer/constants'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import ClinicHeader from '../ClinicHeader'
import { CADeviceUseFixingComponent } from '../ClinicHomePage/components/CADeviceUseFixingComponent'
import {
  AvailabilityPageContainer,
  ClinicManageBookingsContainer,
} from '../ClinicManageBookingsPage/ClinicManageBookingsPageStyles'
import {
  ClinicChoosingContainer,
  ClinicItem,
} from 'src/mentor/components/MentorClinicBookingsPage/MentorClinicBookingStyles'
import { Box, Card, CardContent, Typography, Grid, CircularProgress } from '@mui/material'
import styled from 'styled-components'
import CALandingPageModern from './CALandingPageModern'

// Modern styled components
const ModernPageContainer = styled(AvailabilityPageContainer)`
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
  min-height: 100vh;
  padding-bottom: 40px;
`

const ContentWrapper = styled(Box)`
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  @media (max-width: 768px) {
    padding: 16px;
  }
`

const BrandCard = styled(Card)`
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 2px solid rgba(116, 80, 170, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
  cursor: pointer;
  overflow: hidden;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #7450aa 0%, #5a3d88 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    border-color: rgba(116, 80, 170, 0.3);
    box-shadow: 0 8px 32px rgba(116, 80, 170, 0.2);
    transform: translateY(-4px);

    &::before {
      opacity: 1;
    }
  }
`

const BrandCardContent = styled(CardContent)`
  padding: 32px !important;
  @media (max-width: 768px) {
    padding: 24px !important;
  }
`

const BrandIcon = styled(Box)`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #7450aa 0%, #5a3d88 100%);
  color: white;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(116, 80, 170, 0.3);
`

const BrandName = styled(Typography)`
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 8px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`

const BrandShortName = styled(Typography)`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 16px;
`

const ChooseButton = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #7450aa;
  font-weight: 600;
  font-size: 14px;
  margin-top: 16px;
  transition: all 0.2s ease;

  ${BrandCard}:hover & {
    gap: 12px;
    color: #5a3d88;
  }
`

const EmptyState = styled(Box)`
  text-align: center;
  padding: 80px 24px;
  color: #9ca3af;
`

const EmptyStateIcon = styled(Box)`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(116, 80, 170, 0.1) 0%, rgba(116, 80, 170, 0.05) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
`

const EmptyStateText = styled(Typography)`
  font-size: 18px;
  color: #6b7280;
  margin-top: 16px;
  font-weight: 500;
`

const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`

export default function ClinicLandingPage() {
  const { clinicId } = useParams()
  const navigate = useNavigate()
  const { chosenRoleFlow, ownerData, assistantData } = useContext(ClinicDataContext)
  const [isLoading, setIsLoading] = useState(true)

  let thisClinicData
  if (chosenRoleFlow === ROLE_TYPES.roleClinicOwner) {
    thisClinicData = ownerData?.associatedClinicsData?.find((item) => item.id === clinicId)
  } else if (chosenRoleFlow === ROLE_TYPES.roleClinicAssistant) {
    thisClinicData = assistantData?.associatedClinicsData?.find((item) => item.id === clinicId)
  }

  // Get clinic brands list
  const clinicBrandsList = useMemo(() => {
    if (chosenRoleFlow === ROLE_TYPES.roleClinicOwner) {
      return ownerData?.associatedClinicBrandsData || []
    }
    return []
  }, [chosenRoleFlow, ownerData?.associatedClinicBrandsData])

  // Handle redirects based on brand count
  useEffect(() => {
    if (chosenRoleFlow === ROLE_TYPES.roleClinicOwner) {
      setIsLoading(false)
      
      // If no brands, redirect to onboarding
      if (!clinicBrandsList || clinicBrandsList.length === 0) {
        navigate('/clinic-home/new-brand-onboarding', { replace: true })
        return
      }

      // If only one brand, redirect to that brand's page
      if (clinicBrandsList.length === 1) {
        const singleBrand = clinicBrandsList[0]
        navigate(`/clinic-home/${singleBrand.id}`, { replace: true })
        return
      }
    }
  }, [chosenRoleFlow, clinicBrandsList, navigate])

  if (chosenRoleFlow === ROLE_TYPES.roleClinicAssistant) {
    return <CALandingPageModern assistantData={assistantData} />
  } else if (chosenRoleFlow === ROLE_TYPES.roleClinicOwner) {
    // Show loading state while checking redirects
    if (isLoading) {
      return (
        <ModernPageContainer>
          <ClinicHeader heading="Loading..." />
          <LoadingContainer>
            <CircularProgress sx={{ color: '#7450aa' }} />
          </LoadingContainer>
        </ModernPageContainer>
      )
    }

    // If we have multiple brands, show selection UI
    if (clinicBrandsList && clinicBrandsList.length > 1) {
      return (
        <ModernPageContainer>
          <ClinicHeader heading="Select a Clinic Brand" />
          <ContentWrapper>
            <Grid container spacing={3}>
              {clinicBrandsList.map((item, index) => {
                const brandName = item?.brandName || item?.title || 'Unnamed Brand'
                const brandShortName = item?.brandShortName
                
                return (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <BrandCard onClick={() => navigate(`${item?.id}`)}>
                      <BrandCardContent>
                        <BrandIcon>
                          <Business sx={{ fontSize: 32 }} />
                        </BrandIcon>
                        <BrandName>{brandName}</BrandName>
                        {brandShortName && (
                          <BrandShortName>{brandShortName}</BrandShortName>
                        )}
                        <ChooseButton>
                          Select Brand
                          <ArrowForward sx={{ fontSize: 18 }} />
                        </ChooseButton>
                      </BrandCardContent>
                    </BrandCard>
                  </Grid>
                )
              })}
            </Grid>
          </ContentWrapper>
        </ModernPageContainer>
      )
    }

    // Fallback: should not reach here due to redirects, but just in case
    return (
      <ModernPageContainer>
        <ClinicHeader heading="Choose a Clinic Brand" />
        <ContentWrapper>
          <EmptyState>
            <EmptyStateIcon>
              <Business sx={{ fontSize: 64, color: '#7450aa' }} />
            </EmptyStateIcon>
            <EmptyStateText>No clinic brands found</EmptyStateText>
          </EmptyState>
        </ContentWrapper>
      </ModernPageContainer>
    )
  }
  
  return null
}

const CALandingPage = ({ assistantData, thisClinicData }) => {
  const [CAFlowDialogOpen, setCAFlowDialogOpen] = useState(false)

  let roleCaConfig = localStorage.getItem('ROLE_CA_CONFIG')
  if (roleCaConfig) {
    roleCaConfig = JSON.parse(roleCaConfig)
  }

  const navigate = useNavigate()
  const clinicsList = assistantData?.associatedClinicsData
  const clinicGroups = {}
  clinicsList?.forEach((clinic) => {
    if (clinic?.clinicBrandId) {
      if (clinicGroups[clinic?.clinicBrandId]) {
        clinicGroups[clinic?.clinicBrandId] = [clinic, ...clinicGroups[clinic?.clinicBrandId]]
      } else {
        clinicGroups[clinic?.clinicBrandId] = [clinic]
      }
    } else {
      toast.error('No associated brand')
    }
  })

  useEffect(() => {
    // This component is only for Clinic Assistant portal (/ca-home)
    // Kiosk portal (/kiosk) routing is handled by KioskRoutesHandler
    // Set ROLE_CA_CONFIG with reception-tablet flow for clinic assistant
    const roleCaConfig = {}
    localStorage.setItem('ROLE_CA_CONFIG', JSON.stringify(roleCaConfig))
    // Do not redirect - let the user choose from the landing page
    // The landing page (CALandingPageModern) will handle showing clinics and options
  }, [])

  return (
    <AvailabilityPageContainer>
      <ClinicHeader heading="Choose a Clinic" />
      <div className="w-100 d-flex" style={{ justifyContent: 'right', paddingRight: '16px' }}>
        <SettingsIcon onClick={() => setCAFlowDialogOpen(true)} style={{ cursor: 'pointer' }} />
        <div style={{ position: 'absolute', top: '4px', right: '8px' }}>
          <CADeviceUseFixingComponent
            CAFlowDialogOpen={CAFlowDialogOpen}
            setCAFlowDialogOpen={setCAFlowDialogOpen}
          />
        </div>
      </div>
      {Object.keys(clinicGroups)?.map((clinicBrandId, clinicBrandIdIndex) => {
        const clinicsOfThisBrand = clinicGroups[clinicBrandId]
        return (
          <ClinicManageBookingsContainer style={{ marginTop: '50px' }} key={clinicBrandIdIndex}>
            <ClinicChoosingContainer>
              {clinicsOfThisBrand?.map((item, index) => {
                return (
                  <ClinicItem
                    key={index}
                    onClick={() => navigate(`${item?.clinicBrandId}/${item?.id}`)}
                  >
                    <div>
                      <b>{item?.name}</b>
                    </div>
                    <div>{item?.location?.address}</div>
                    <div>{item?.location?.city}</div>
                    <div></div>
                    <button style={{ background: '#fff', borderRadius: '8px' }}>Choose</button>
                  </ClinicItem>
                )
              })}
            </ClinicChoosingContainer>
          </ClinicManageBookingsContainer>
        )
      })}
    </AvailabilityPageContainer>
  )
}
